import { z } from "zod";
import { situationRoleSchema } from "../../types/brainstorm.js";
import {
  scenarioStatusEnum,
  scenarioValidationStatusEnum,
} from "../../types/enums.js";
import { taskCriticalitySchema } from "../../validation/finding-metrics.js";
import { entityBaseSchema, slugSchema } from "./common.js";

/**
 * Human-test question — questions the walkthrough must answer at the end.
 * Mirrors cloud `outlines.human_tests` JSONB shape.
 */
export const humanTestSchema = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
});
export type HumanTest = z.infer<typeof humanTestSchema>;

/**
 * Outline frontmatter — describes a situation in pure domain language.
 * Mirrors `outlines` table in cloud schema (minus tenant/project columns).
 */
export const outlineFrontmatterSchema = entityBaseSchema.extend({
  /** Corresponds to cloud `name`. */
  title: z.string().min(1),
  description: z.string().nullish(),
  /**
   * Legacy singular capability ref — accepted for one release cycle so files
   * authored before multi-capability outlines still parse. Readers promote
   * the value to `primary_capability`; writers always emit the new shape.
   * @deprecated use `primary_capability` + `additional_capabilities`
   */
  capability: slugSchema.nullish(),
  /** Primary capability slug — drives grouping and report layout. */
  primary_capability: slugSchema.nullish(),
  /** Secondary capability slugs — cross-cutting tags (permissions, a11y, billing, etc.). */
  additional_capabilities: z.array(slugSchema).default([]),
  narrative: z.string().nullish(),
  complexity_level: z.string().nullish(),
  language: z.string().default("en"),
  status: scenarioStatusEnum.default("draft"),
  validation_status: scenarioValidationStatusEnum.nullish(),
  task_criticality: taskCriticalitySchema.default("important"),
  /** Structural tension driving this situation, in "X vs. Y" form. Null when no genuine tension exists. */
  tension: z.string().nullish(),
  situation_roles: z.array(situationRoleSchema).default([]),
  human_tests: z.array(humanTestSchema).default([]),
});

export type OutlineFrontmatter = z.infer<typeof outlineFrontmatterSchema>;
